import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ApisEnum } from '../enums/apis.enum';
import { ChangeSuggestions } from '../interfaces/portfolio';
import { ClientModel } from './../interfaces/client-model';
import { AppConfigService } from './app-config.service';
import { AppUpdateService } from './app-update.service';
import { StorageService as storage } from './storage.service';
import { UtilService } from './util.service';
@Injectable({
  providedIn: 'root'
})
export class ClientService {
  clientData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  accountData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  rbAccountData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  phoneData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  pixData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  connectionData: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  config: any;
  constructor(
    private http: HttpClient,
    private router: Router,
    private utilService: UtilService,
    private appUpdateService: AppUpdateService,
    private configService: AppConfigService
  ) {
    this.config = this.configService.config;
  }

  redirectLandingPage() {
    window.open(`${this.config.API_PROT}://${this.config.API_HOST}`, '_top', '')
  }

  /**
   * Check App Update
   * @param res Response
   */
  private checkAppUpdate(res: Response | any): Response {
    if (res.headers.has('x-appversion')) {
      this.appUpdateService.appVersion.emit(res.headers.get('x-appversion'));
    }
    return res;
  }

  /**
   * RESET CLIENT DATA
   */
  resetData() {
    this.clientData.next(null);
    this.accountData.next(null);
    this.phoneData.next(null);
    this.pixData.next(null);
    storage.clear();
  }

  create(client: ClientModel): Promise<ClientModel> {
    storage.clearSection();
    const url = this.utilService.makeUri([ApisEnum.Client, this.config.PARTNER_UUID]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    client.bank_uuid = this.config.BANK_UUID;
    const dataPost = this.utilService.buildPostData(client);

    return this.http.post<ClientModel>(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then((res: any) => {
        this.checkAppUpdate(res);
        if (res.status === 200)
          res.body.real_client = true

        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Client Login
   * @param data Data Client Login
   */
  login(data: Object): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, this.config.PARTNER_UUID, 'auth']);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  /**
   * PUT ENTITY
   * @param uri call service api
   * @param client_uuid Id api call
   * @param entity_id Id from entity
   * @param data Data Client Phone
   * @param jwt Client JWT
   */
  update(data: ClientModel, client_uuid?: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid || storage.getClientUuid(), { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * GET ALL ENTITY
   * @param uri call service api
   * @param client_uuid Client UUID 
   * @param jwt Client JWT
   */
  read(client_uuid?: string, jwt?: string): Promise<ClientModel> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid || storage.getClientUuid(), { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getClients(data: any) {
    const url = this.utilService.makeUri(['clients', data, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Client Information
   * @param client_uuid Client UUID
   * @param jwt Client JWT
   */
  getInfo(client_uuid: string, jwt?: string): Promise<ClientModel> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid || storage.getClientUuid(), { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * GET ENTITY BY ID
   * @param uri call service api
   * @param client_uuid Client UUID 
   * @param entity_id Id from entity
   * @param jwt Client JWT
   */
  readById(client_uuid?: string, jwt?: string): Promise<ClientModel> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid || storage.getClientUuid(), { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  closeAccount(account_uuid): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, storage.getClientUuid(), 'account', account_uuid, 'close', { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Planejamento
   */
  getGoals(client_uuid: string, filter?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'goals', filter, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  deleteGoal(client_uuid: string, goal_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri(['client', client_uuid, 'pms', 'goal', goal_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.delete(url, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => this.utilService.handleError(err, this.router));
  }

  updateGoal(client_uuid: string, goal_id: string, data: Object, filter?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'goal', goal_id, filter, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.put(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  createGoal(data: Object, filter?: any, jwt?: string): Promise<any> {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'goal', filter, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Patrimônio
   */
  getIncomes(client_uuid: string, filter?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'incomes', filter, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Meus investimentos
   */

  getPositions(client_uuid: string, filter?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'positions', filter, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getPositionsByAccount(client_uuid: string, rb_account_uuid?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'positions', rb_account_uuid, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getAccountPositions(client_uuid: string, filter?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', 'positions', filter, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getPositionHistory(rb_account_uuid: any, filter?: any, jwt?: string): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'position-history', filter, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => {
        return { histories: [] }
      });
  }

  /**
   * Performance
   */
  getPerformance(client_uuid: string, data?: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'performance-historical', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }


  /**
   * Get Client Bank Accounts
   * @param data Data Search
   */
  getBankAccounts(client_uuid: string, data?: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'accounts', { jwt: jwt || storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Account Balance
   * @param account_uuid 
   */
  getAccountBalance(client_uuid: string, account_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'balance', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  getAssets(data) {
    const url = this.utilService.makeUri([ApisEnum.Partner, 'pms', 'assets', { jwt: storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Client Allocations
   * @param client_uuid
   */
  getAllocations(client_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'allocations', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getInvestorAccounts(filter?: any) {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'investor', 'accounts', filter, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateAllocations(starting_cash: number, final_cash: number, assetsToAdd: Array<{
    weights: number,
    partner_asset_uuid: string
  }>, filter?: any): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const data = {
      allocation_type: 'model',
      assets: assetsToAdd,
      extra: {
        "incomes_simulated": [
          {
            "type": "simulated",
            "value": -final_cash
          },
          {
            "type": "position",
            "value": starting_cash
          }
        ]
      }
    }
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'allocation', filter, { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateAllocations_new(income_id: number, act_date: string, name: string, total_value: number, allocation_type: string, assetsToAdd: Array<{
    weights: number,
    partner_asset_uuid: string
  }>, filter?: any): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const data = {
      allocation_type: allocation_type,
      assets: assetsToAdd,
      extra: {
        "incomes_simulated": [
          {
            "id": income_id,
            "date": act_date,
            "name": name,
            "type": "simulated",
            "tenor": 0,
            "value": total_value,
            "frequency": 1,
            "repetition": 0,
            "numeraire": "5107929e-29d6-4e09-8c9b-20d25b8df4c3",
            "tenor_type": "actual_actual"
          }
        ]
      }
    }
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'allocation', filter, { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }


  getChangeSuggestionsAllocations(filter?: any): Promise<ChangeSuggestions> {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'allocation', 'change-suggestion', filter, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getClassificationEnum(jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Partner, 'pms', 'classification-enum', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Update Password
   * @param data Data Client Password Update
   * @param jwt Client JWT
   */
  updatePassword(data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, 'password', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Client Account Statements
   * @param client_uuid Client UUID
   * @param account_uuid  Client Account UUID
   * @param jwt JSON Web Token
   */
  getAccountStatements(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid || storage.getClientUuid(), 'account', account_uuid, 'statements', { jwt: jwt || storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
  * Get Client Account Statements PDF
  * @param client_uuid Client UUID
  * @param account_uuid  Client Account UUID
  * @param jwt JSON Web Token
  */
  getAccountStatementsPDF(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'statements', 'pdf', { jwt: jwt || storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Account Boleto Information
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param data Data Boleto information
   * @param jwt JSON Web Token
   */
  getAccountBoletoInfo(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'boleto', { jwt: jwt || storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Pay Boleto
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param data Data Boleto information
   * @param jwt JSON Web Token
   */
  payAccountBoleto(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'boleto', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  /**
   *
   * @param client_uuid Client UUID
   * @param data Client Data Phone
   * @param jwt JSON Web Token
   */
  createPhone(client_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'phone', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   *
   * @param client_uuid Client UUID
   * @param phone_id Phone ID
   * @param data Data Phone to Update
   * @param jwt JSON Web Token
   */
  updatePhone(client_uuid: string, phone_id: number, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'phone', phone_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Send Phone SMS Verify
   * @param client_uuid Client UUID
   * @param phone_id Phone ID
   * @param jwt JSON Web Token
   */
  sendPhoneSmsVerify(client_uuid: string, phone_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'phone', phone_id, 'verify', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.post(url, null, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Check Phone SMS Verify
   * @param client_uuid Client UUID
   * @param phone_id Phone ID
   * @param sms_code SMS Code
   * @param jwt JSON Web Token
   */
  checkPhoneSmsVerify(client_uuid: string, phone_id: number, sms_code: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'phone', phone_id, 'verify', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData({ sms_code: sms_code });

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  emailVerify(client_uuid?: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid || storage.getClientUuid(), 'email', 'verify', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.post(url, {}, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  emailVerifyToken(client_uuid: string, jwt: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'email', 'verify', { jwt: jwt }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  emailVerifyDocument(client_uuid: string, document_number: any, jwt: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'email', 'verify', { jwt: jwt }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData({ document_number: document_number });

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Client Phones
   * @param client_uuid Client UUID
   * @param data Data Query
   * @param jwt JSON Web Token
   */
  getPhones(client_uuid: string, data?: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'phones', { jwt: jwt || storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get a Client Phone
   * @param client_uuid Client UUID
   * @param phone_id Client Phone ID
   * @param jwt JSON Web Token
   */
  getPhone(client_uuid: string, phone_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'boleto', phone_id, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Create Account Favorite
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param data Favorite Data
   * @param jwt JSON Web Token
   */
  createAccountFavorite(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'favorite', { jwt: jwt || storage.getClientJwt() }]);

    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  /**
   *
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param favorite_id Favorite ID
   * @param data Favorite Data
   * @param jwt JSON Web Token
   */
  updateAccountFavorite(client_uuid: string, account_uuid: string, favorite_id: number, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'favorite', favorite_id, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.put(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  /**
   * Get Client Account Favorites
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param jwt JSON Web Token
   */
  getAccountFavorites(client_uuid: string, account_uuid: string, data?: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'favorites', { jwt: jwt || storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get a Client Account Favorite
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param favorite_id Favorite ID
   * @param jwt JSON Web Token
   */
  getAccountFavorite(client_uuid: string, account_uuid: string, favorite_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'favorite', favorite_id, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   *
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param favorite_id Favorite ID
   * @param jwt JSON Web Token
   */
  removeAccountFavorite(client_uuid: string, account_uuid: string, favorite_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'favorite', favorite_id, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Client Account External Transfer
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param data Data Transfer
   * @param jwt JSON Web Token
   */
  clientAccountExternalTransfer(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'external-transfer', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get List External Transfers
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param jwt JSON Web Token
   */
  getClientAccountExternalTransfers(client_uuid: string, account_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'external-transfers', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get a Client External Transfer
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param transfer_id Transfer ID
   * @param jwt JSON Web Token
   */
  getClientAccountExternalTransferById(client_uuid: string, account_uuid: string, transfer_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'external-transfer', transfer_id, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Client Account Internal Transfer
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param data Data Transfer
   * @param jwt JSON Web Token
   */
  clientAccountInternalTransfer(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'transfer', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get List Internal Transfers
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param jwt JSON Web Token
   */
  getClientAccountInternalTransfers(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'transfers', { jwt: jwt || storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get a Client Internal Transfer
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param transfer_id Transfer ID
   * @param jwt JSON Web Token
   */
  getClientAccountInternalTransfer(client_uuid: string, account_uuid: string, transfer_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'transfer', transfer_id, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Client Account PIX Transfer
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param data Data Transfer
   * @param jwt JSON Web Token
   */
  clientAccountPIXTransfer(client_uuid: string, account_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'transfer', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  /**
   * Get a Client PIX Transfer
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param transfer_id Transfer ID
   * @param jwt JSON Web Token
   */
  getClientAccountPIXTransfer(client_uuid: string, account_uuid: string, transfer_id: number, data: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'transfer', transfer_id, { jwt: jwt || storage.getClientJwt() }]);
    const dataPost = this.utilService.buildPostData(data);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteClientAccountPIXTransfer(client_uuid: string, account_uuid: string, transfer_id, jwt?: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'transfer', transfer_id, 'schedule', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get List PIX Transfers
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param jwt JSON Web Token
   */
  getClientAccountPIXTransfers(client_uuid: string, account_uuid: string, data?: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'statements', data, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Consulta chaves PIX LadyBank
   * @param client_uuid
   * @param account_uuid
   * @param jwt
   * @returns
   */
  getClientAccountPIXs(client_uuid: string, account_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pixs', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Validar chave PIX para transferência
   * @param client_uuid Client UUID
   * @param account_uuid Client Account UUID
   * @param transfer_id Transfer ID
   * @param jwt JSON Web Token
   */
  getClientAccountPIXSearch(client_uuid: string, account_uuid: string, key: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'search', { jwt: jwt || storage.getClientJwt(), key: key }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getClientAccountPIXStatements(client_uuid: string, account_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'statements', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Criação de chave PIX
   * @param client_uuid
   * @param account_uuid
   * @param data
   * @param jwt
   * @returns
   */
  createClientAccountPIXKey(client_uuid: string, account_uuid: string, data: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteClientAccountPIXKey(client_uuid: string, account_uuid: string, key, jwt?: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', key, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Create a Client Address
   * @param client_uuid Client UUID
   * @param data Data Address
   * @param jwt JSON Web Token
   */
  createClientAddress(client_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'address', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Edit a Client Address
   * @param client_uuid Client UUID
   * @param address_id Address ID
   * @param data Data Address
   * @param jwt JSON Web Token
   */
  editClientAddress(client_uuid: string, address_id: number, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'address', address_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get a Client Address
   * @param client_uuid Client UUID
   * @param address_id Address ID
   * @param jwt JSON Web Token
   */
  getClientAddress(client_uuid: string, address_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'address', address_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.get(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Client Addresses
   * @param client_uuid Client UUID
   * @param jwt JSON Web Token
   */
  getClientAddresses(client_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'addresses', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.get(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Client Files
   * @param client_uuid Client UUID
   * @param jwt JSON Web Token
   */
  getClientFiles(client_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'upload-file', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.get(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get ThirdParty GrediGo Account History
   * @param client_uuid Client UUID
   * @param account_id Account ID
   * @param jwt JSON Web tokne
   */
  getThirdPartyCredigoAccounthistory(client_uuid: string, account_id: number, data?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'third_party', 'credigo', 'account', account_id, 'history', data, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.get(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get ThirdParty GrediGo Categories
   * @param client_uuid Client UUID
   * @param jwt JSON Web Token
   */
  getThirdPartyCredigoCategories(client_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'third_party', 'credigo', 'categories', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.get(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Delete a ThirdParty Credigo Account
   * @param client_uuid Client UUID
   * @param account_id Account ID
   * @param jwt JSON Web Token
   */
  deleteThirdPartyCredigoAccount(client_uuid: string, account_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'third_party', 'credigo', 'account', account_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.delete(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   *
   * @param client_uuid Client UUID
   * @param account_id Account ID
   * @param data Data Update
   * @param jwt JSON Web Token
   */
  updateThirdPartyCredigoAccount(client_uuid: string, account_id: number, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'third_party', 'credigo', 'account', account_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get News
   * @param client_uuid Client UUID
   * @param jwt JSON Web Token
   */
  getNews(client_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'news', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.get(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Create Client Category
   * @param client_uuid Client UUID
   * @param data Category Data
   * @param jwt JSON Web Token
   */
  createClientCategory(client_uuid: string, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'category', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Update Client Category
   * @param client_uuid Client UUID
   * @param category_id Category ID
   * @param data Category Data
   * @param jwt JSON Web Token
   */
  updateClientCategory(client_uuid: string, category_id: number, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'category', category_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get a Client Category
   * @param client_uuid Client UUID
   * @param category_id Category ID
   * @param jwt JSON Web Token
   */
  getClientCategory(client_uuid: string, category_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'category', category_id, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Delete a Client Category
   * @param client_uuid Client UUID
   * @param category_id Category ID
   * @param jwt JSON Web Token
   */
  deleteClientCategory(client_uuid: string, category_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'category', category_id, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Get Client Categories (statement)
   * @param client_uuid Client UUID
   * @param jwt JSON Web Token
   */
  getClientCategories(client_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'categories', { jwt: jwt || storage.getClientJwt() }]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * atualizar categoria do extrato
   * @param client_uuid
   * @param data
   * @param jwt
   * @returns
   */
  createClientStatementCategory(client_uuid: string, data: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'statement', 'category', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateClientStatementCategory(client_uuid: string, data: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'statement', 'category', data.ref_statement_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Delete Client Category Statement
   * @param client_uuid Client UUID
   * @param category_id Category ID
   * @param ref_statement_id Reference Statement ID
   * @param jwt JSON Web Token
   */
  deleteClientCategoryStatement(client_uuid: string, category_id: number, ref_statement_id: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'category', category_id, 'statement', ref_statement_id, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }
  /**
   * Agendar transferências
   * @param client_uuid
   * @param account_uuid
   * @param data
   * @param jwt
   * @returns
   */
  createPaymentSchedule(client_uuid: string, account_uuid: string, data: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'payment', 'schedule', { jwt: jwt || storage.getClientJwt() }]);

    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  getPaymentsSchedules(client_uuid: string, account_uuid: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'payment', 'schedules', { jwt: jwt || storage.getClientJwt() }]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updatePaymentSchedule(client_uuid: string, account_uuid: string, schedule_id, data: Object, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'payment', 'schedule', schedule_id, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.put(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deletePaymentSchedule(client_uuid: string, account_uuid: string, schedule_id, jwt?: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'payment', 'schedule', schedule_id, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => {
        this.utilService.handleError(err, this.router)
        return err
      });
  }

  getOrders(client_uuid: string, rb_account_uuid?: any, jwt?: string): Promise<any> {

    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'orders', rb_account_uuid, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Mostrar as notificações
   * @param client_uuid
   * @param jwt
   * @returns
   */
  getMessages(client_uuid: string, params?: any, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'messages', params, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   *
   * @param client_uuid Buscar as profissões
   * @param profession
   * @param jwt
   * @returns
   */
  getCob(client_uuid: string, data?: any, jwt?: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'cbo', data, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  /**
   * Sync Client Account CrediGO
   * @param client_uuid Client UUID
   * @param account_id Account ID
   * @param jwt Client JWT
   * @returns
   */
  syncClientAccountCrediGo(client_uuid: string, account_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'third_party', 'credigo', 'account', account_id, 'sync', { jwt: jwt || storage.getClientJwt() }]);

    return this.http.post(url, {}, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getNumeraires(num) {
    switch (num) {
      case 'BRL':
        return this.config.NUMERAIRES.CASH_BRL;
      case 'CDI':
        return this.config.NUMERAIRES.CDI_FACTOR;
      case 'EUR':
        return this.config.NUMERAIRES.CASH_EUR;
      case 'IPCA':
        return this.config.NUMERAIRES.IPCA_FACTOR;
      case 'USD':
        return this.config.NUMERAIRES.CASH_USD;
      case 'BUDGET_CASH_IN_BRL':
        return this.config.NUMERAIRES.BUDGET_CASH_IN_BRL;
      default:
        break;
    }
  }

  getBudgets(filter?: any, jwt?: string) {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'budgets', filter, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveBudget(data, jwt?) {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'budget', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateBudget(budget_id: number, data: any, jwt?: string): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid || storage.getClientUuid(), 'budget', budget_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  removeBudget(budget_id: string, jwt?: string): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'budget', budget_id, { jwt: jwt || storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  createBudgetAssociation(data: any, jwt?: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'budget', 'association', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateBudgetAssociation(client_budget_association_id: number, data: any, jwt?: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'budget', 'association', client_budget_association_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  removeBudgetAssociation(client_budget_association_id: number, jwt?: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'budget', 'association', client_budget_association_id, { jwt: jwt || storage.getClientJwt() }]);

    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getBudgetAssociations(filter?: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'budget', 'associations', filter, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getListBrokers(jwt?: string) {
    const url = this.utilService.makeUri(['brokers', { jwt: jwt || storage.getClientJwt() }]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getListFactors(jwt?: string, factor?: string) {
    const url = this.utilService.makeUri(['pms/assets' + '?asset_name=' + factor + '&' + 'jwt=' + jwt]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveBroker(client_uuid, broker_uuid, data, jwt?) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'broker', broker_uuid, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getBrokers(client_uuid: string, jwt?: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'brokers', { jwt: jwt || storage.getClientJwt() }]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  removeBroker(client_uuid: string, broker_id: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'broker', broker_id, { jwt: storage.getClientJwt() }]);

    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getPmsBrokers(client_uuid: string, jwt?: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'brokers', { jwt: jwt || storage.getClientJwt() }]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  receivedCard(client_uuid: string, account_id: string, card_id: string, data: any) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_id, 'card-pre', 'card', card_id, 'receive', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  trackCard(client_uuid: string, account_uuid: string, card_id: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'card-pre', 'card', card_id, 'track', { jwt: storage.getClientJwt() }]);

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveCard(client_uuid: string, account_id: string, data: any, jwt?: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_id, 'card-pre', 'card', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  readCards(client_uuid: string, account_id: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_id, 'card-pre', 'cards', { jwt: jwt || storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  updateCard(client_uuid: string, account_uuid: string, card_id: string, data: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'card-pre', 'card', card_id, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  activateCard(client_uuid: string, account_uuid: string, card_id: string, data: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'card-pre', 'card', card_id, 'activation', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  changePin(client_uuid: string, account_uuid: string, card_id: string, data: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'card-pre', 'card', card_id, 'pin', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getQRCodePix(client_uuid: string, account_uuid: string, emv: string, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'qrcode', { jwt: jwt || storage.getClientJwt() }, { emv: emv }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  generateQRCode(client_uuid: string, account_uuid: string, data?: any, jwt?: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'qrcode', { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getClaims(client_uuid: string, account_uuid: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'claims', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteClaim(client_uuid: string, account_uuid: string, key_uuid: string, reason: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'claims',
    { id: key_uuid, reason: reason, jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  postClaims(client_uuid: string, account_uuid: string, data: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'claims', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const dataPost = this.utilService.buildPostData(data);

    return this.http.post(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  putClaims(client_uuid: string, account_uuid: string, data: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'claims', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  verifyPixKey(client_uuid: string, account_uuid: string, key: string, key_type: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'verify', { key: key, key_type: key_type, jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  checkPixVerify(client_uuid: string, account_uuid: string, data: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'verify', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getLimits(client_uuid: string, account_uuid: string) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'limits', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  updateLimits(client_uuid: string, account_uuid: string, data) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'pix', 'limits', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    const dataPost = this.utilService.buildPostData(data);

    return this.http.put(url, dataPost, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => { return this.utilService.handleError(err, this.router) });
  }

  getBanksWithIspb(params?) {
    const url = this.utilService.makeUri(['institutions', params, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  generateBankSplip(client_uuid: string, account_uuid: string, data?: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'deposit-boleto', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getListBankSplip(client_uuid: string, account_uuid: string, data: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'deposit-boletos', { jwt: storage.getClientJwt() }, data]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getSettings(client_uuid, type = 'dashboard') {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'settings', { type: type, jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveSettings(client_uuid, data) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'setting', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })

    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateSettings(client_uuid, setting_id, data) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'setting', setting_id, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getRobobankerAccounts(client_uuid: string, filter?: any) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'robobanker', 'accounts', filter, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getRobobankerAccountBydId(client_uuid, robobanker_account_id) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'robobanker', 'account', robobanker_account_id, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  removeRobobankerAccount(client_uuid, robobanker_account_id) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'robobanker', 'account', robobanker_account_id, { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  createRobobankerAccount(client_uuid, robobanker_account_id, data) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'robobanker', 'account', robobanker_account_id, { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateRobobankerAccount(client_uuid, robobanker_account_id, data) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'robobanker', 'account', robobanker_account_id, { jwt: storage.getClientJwt() }]);
    return this.http.put(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  rebalance(client_uuid, robobanker_account_id, data): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'rebalance', { rb_account_uuid: robobanker_account_id, jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  savePmsOrder(client_uuid, partner_asset_uuid, data) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'order', 'partner_asset', partner_asset_uuid, { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getOrder(client_uuid, order_uuid, asset_uuid) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'order', order_uuid, 'asset', asset_uuid, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteOrder(order_uuid) {
    const url = this.utilService.makeUri([ApisEnum.Client, storage.getClientUuid(), 'pms', 'order', order_uuid, { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  createFeeder(client_uuid, rb_account_uuid, data) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'feeder', { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateFeeder(client_uuid, rb_account_uuid, portfolio_feeder_id, data) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'feeder', portfolio_feeder_id, { jwt: storage.getClientJwt() }]);
    return this.http.put(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteFeeder(client_uuid, rb_account_uuid, portfolio_feeder_id) {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'feeder', portfolio_feeder_id, { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getFeederAccount(rb_account_uuid: string, filter?: any) {
    const url = this.utilService.makeUri([ApisEnum.Client, storage.getClientUuid(), 'pms', 'account', rb_account_uuid, 'feeder', 'account', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getFeeders(rb_account_uuid: string, filter?) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'feeders', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getFeedersPositions(rb_account_uuid, filter?) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'feeders-positions', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveGoalTaxLot(client_uuid: string, data, filter?: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'goal-tax-lot', filter, { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteGoalTaxLot(client_uuid: string, goal_tax_lot_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', 'goal-tax-lot', goal_tax_lot_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.delete(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveIncomeTaxLot(client_uuid: string, data, filter?: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'income-tax-lot', filter, { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveIncome(data, filter?: any): Promise<any> {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'income', filter, { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateIncome(income_id: number, data: any, filter?: any): Promise<any> {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'income', income_id, filter, { jwt: storage.getClientJwt() }]);
    return this.http.put(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteIncome(income_id: string, filter?: any): Promise<any> {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'income', income_id, filter, { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  savePosition(rb_account_uuid: string, asset_uuid: string, data: any): Promise<any> {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'position', asset_uuid, { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteIncomeTaxLot(client_uuid: string, income_tax_lot_id: number, jwt?: string): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', 'income-tax-lot', income_tax_lot_id, { jwt: jwt || storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.delete(url, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  updateTaxLot(client_uuid: string, rb_account_uuid: string, tax_lot_id: number, data: any): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'position', 'tax-lot', tax_lot_id, { jwt: storage.getClientJwt() }]);
    return this.http.put(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteTaxLot(client_uuid: string, rb_account_uuid: string, tax_lot_id: number): Promise<any> {
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'pms', 'account', rb_account_uuid, 'position', 'tax-lot', tax_lot_id, { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  // OPEN FINANCE
  getLinaOrganizations(filter?: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'openfinance', 'organizations', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getLinaConsent(data: any): Promise<any> {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'openfinance', 'consent', { jwt: storage.getClientJwt() }]);
    return this.http.post(url, data, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getOpenFinanceConsents(filter?: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'openfinance', 'consents', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getOpenFinanceAccounts(filter?: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'openfinance', 'accounts', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  openFinanceValidation(client_openfinance_id: number) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'openfinance', 'consent', client_openfinance_id, 'validation', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, {}, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  openFinanceConsentSync(data?: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'openfinance', 'consent', 'sync', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  openFinanceSync(client_openfinance_id: number) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'openfinance', 'sync', client_openfinance_id, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, {}, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  getInvestments(filter?: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', 'investments', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  linkAccountInvestment(connection_uuid: string, investment_uuid: string, rb_account_uuid: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'investment', investment_uuid, 'rb_account', rb_account_uuid, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, {}, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  getTransactions(filter: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', 'transactions', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getTransactionsByAccount(account_uuid: string, filter?: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'transactions', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteAccountTransaction(connection_uuid: string, account_uuid: string, transaction_uuid: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'account', account_uuid, 'transaction', transaction_uuid, { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  saveAccountTransaction(connection_uuid: string, account_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'account', account_uuid, 'transaction', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  updateAccountTransaction(connection_uuid: string, account_uuid: string, transaction_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'account', account_uuid, 'transaction', transaction_uuid, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  getCards(filter: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', 'cards', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getTransactionsByCards(account_uuid: string, filter?: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'card', account_uuid, 'transactions', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res?.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveInvestmentTransaction(connection_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'investment', 'transaction', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  updateInvestmentTransaction(connection_uuid: string, investment_uuid: string, transaction_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'investment', investment_uuid, 'transaction', transaction_uuid, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  deleteInvestmentTransaction(connection_uuid: string, investment_uuid: string, transaction_uuid: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'investment', investment_uuid, 'transaction', transaction_uuid, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.delete(url, { observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  saveAccountConnection(connection_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'account', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  updateAccountConnection(connection_uuid: string, account_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'account', account_uuid, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  deleteAccountConnection(connection_uuid: string, account_uuid: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'account', account_uuid, { jwt: storage.getClientJwt() }]);

    return this.http.delete(url, { observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  saveCardConnection(connection_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'card', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  deleteCardConnection(connection_uuid: string, card_uuid: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'card', card_uuid, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.delete(url, { observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  saveCardTransaction(connection_uuid: string, card_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'card', card_uuid, 'transaction', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  updateCardTransaction(connection_uuid: string, card_uuid: string, transaction_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'card', card_uuid, 'transaction', transaction_uuid, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  deleteCardTransaction(connection_uuid: string, card_uuid: string, transaction_uuid: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'card', card_uuid, 'transaction', transaction_uuid, { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  createConnection(data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  updateConnection(connection_uuid: string, data: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.put(url, data, { headers, observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  deleteConnection(connection_uuid: string) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, { jwt: storage.getClientJwt() }]);
    return this.http.delete(url, { observe: 'response' }).toPromise().then(res => {
      this.checkAppUpdate(res);
      return res.body;
    }).catch(err => {
      this.utilService.handleError(err, this.router)
      return err
    });
  }

  getConnections(filter?: any) {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connections', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getConnectionAccounts(filter?: any) {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', 'accounts', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { params: filter, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getAccount(account_uuid: string, filter?: any): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'account', account_uuid, 'statements', 'pdf', filter, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveAccountToConnection(connection_uuid: string, rb_account_uuid: string, data: any = {}) {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'rb_account', rb_account_uuid, { jwt: storage.getClientJwt() }])
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  deleteAssociatedAccount(connection_uuid: string, rb_account_uuid: string) {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'rb_account', rb_account_uuid, { jwt: storage.getClientJwt() }])
    return this.http.delete(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getConnectionAssociations(connection_uuid: string, filter?: any) {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'associations', filter, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  saveConnectionAssocation(connection_uuid: string, body: any) {
    const client_uuid = storage.getClientUuid()
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'connection', connection_uuid, 'association', { jwt: storage.getClientJwt() }])
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.http.post(url, body, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router))
  }

  getQuizQuestions(filter?: any): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'quiz', 'generate', filter, { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  submitQuiz(data: any): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'quiz', { jwt: storage.getClientJwt() }]);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(url, data, { headers, observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }

  getQuizLadderHistory(): Promise<any> {
    const client_uuid = storage.getClientUuid();
    const url = this.utilService.makeUri([ApisEnum.Client, client_uuid, 'quiz', 'generate', { jwt: storage.getClientJwt() }]);
    return this.http.get(url, { observe: 'response' })
      .toPromise()
      .then(res => {
        this.checkAppUpdate(res);
        return res.body;
      })
      .catch(err => this.utilService.handleError(err, this.router));
  }
}
